import * as React from 'react';

import { Swiper } from 'swiper';
import { Navigation, Pagination, Virtual } from 'swiper/modules';
import { SwiperProps } from 'swiper/react';

import { useWindowSize } from '@hooks/use-window-size';

import { Button } from '../button';
import { SliderBullets } from './components/slider-bullets';
import * as Styled from './slider.styled';

type OwnProps = {
	children: React.ReactNode;
	id?: string;
	darkNav?: boolean;
} & SwiperProps;

const CustomNavigation = React.memo(
  ({
    darkNav,
    isHidden,
    className,
    swiper
  }: {
    darkNav?: boolean;
    isHidden: boolean;
    swiper: any;
    className?: string;
  }) => {
    const [isPrev, setIsPrev] = React.useState<boolean>(false);
    const [isNext, setIsNext] = React.useState<boolean>(true);

    React.useEffect(() => {
      if (!swiper) return;

      const updateNavState = () => {
        if (swiper.loopedSlides !== undefined) return; 
        
        setIsPrev(!swiper.isBeginning);
        setIsNext(!swiper.isEnd);
      };

      updateNavState();

      swiper.on('slideChange', updateNavState);
      swiper.on('beforeTransitionStart', updateNavState); 

      return () => {
        swiper.off('slideChange', updateNavState);
        swiper.off('beforeTransitionStart', updateNavState);
      };
    }, [swiper]);

    return (
      <Styled.SliderControls className={className} $isHidden={isHidden} $isPrev={isPrev} $isNext={isNext}>
        <Button
          variant='round'
          color={darkNav ? 'neutral' : 'light'}
          hasIcon
          iconPosition='start'
          onClick={() => swiper.slidePrev()}
        />
        <Button
          variant='round'
          color={darkNav ? 'neutral' : 'light'}
          hasIcon
          iconPosition='end'
          onClick={() => swiper.slideNext()}
        />
      </Styled.SliderControls>
    );
  }
);

const Slider: React.FC<OwnProps> = ({ children, id, darkNav, ...swiperProps }) => {
	const { width } = useWindowSize();
	const [shouldShowNavigation, setShouldShowNavigation] = React.useState(false);
	const [swiper, setSwiper] = React.useState<any>(null);
  const [isReady, setIsReady] = React.useState(false);

	React.useEffect(() => {
    if (!width) return;

    const breakpointValues = Object.entries(swiperProps.breakpoints || {})
      .sort(([a], [b]) => Number(b) - Number(a))
      .find(([breakpoint]) => width >= Number(breakpoint));

    let slidesPerView = breakpointValues
      ? (breakpointValues[1] as any).slidesPerView
      : swiperProps.slidesPerView;

    if (!slidesPerView) {
      slidesPerView = 1;
    }

    const childrenCount = React.Children.count(children);

    const isNeedNavigation =
      slidesPerView === 'auto'
        ? childrenCount > 1 
        : childrenCount > Number(slidesPerView);

    setShouldShowNavigation(isNeedNavigation);
    setIsReady(true);
  }, [width, children, swiperProps.breakpoints, swiperProps.slidesPerView]);

  const isNavigationActive = swiperProps.navigation !== false && shouldShowNavigation;

	return (
		<Styled.Wrapper $isReady={isReady}>
			<Styled.SwiperStyled
				$hasNavigation={isNavigationActive}
				modules={[Navigation, Virtual, Pagination]}
				pagination={{
					el: '#containerForBullets-' + id,
					type: 'bullets',
					bulletClass: 'swiper-custom-bullet',
					bulletActiveClass: 'swiper-custom-bullet-active',
					clickable: true
				}}
				{...swiperProps}
				onSwiper={(swiperInstance: Swiper) => {
					setSwiper(swiperInstance);
					swiperProps.onSwiper?.(swiperInstance);

          if (swiperInstance.isLocked) {
            setShouldShowNavigation(false);
         }
				}}
			>
				{children}

				{swiperProps.pagination !== false && <SliderBullets id={id} isHidden={!shouldShowNavigation} />}
			</Styled.SwiperStyled>
			{isNavigationActive && (
				<CustomNavigation darkNav={darkNav} isHidden={!shouldShowNavigation} swiper={swiper} />
			)}
		</Styled.Wrapper>
	);
};

export default React.memo(Slider);
